import React from 'react';

import {
  Bot,
  ChevronsLeftRightEllipsis,
  Group,
  Import,
  Lightbulb,
  SquareChevronUp,
  Star,
  Users,
} from 'lucide-react';

import { StaticColors } from 'common/colors/constants';
import { publicReviewIntegrations } from 'common/constants/integrations';
import {
  CollectFeedbackOptionValue,
  IntegrationOptionValue,
  MotivationOptionValue,
  SourcesOptionValue,
} from 'common/subdomain/admin/AdminOnboarding/constants';
import devURL from 'common/util/devURL';

import OnboardingFeedbackSources, {
  isSelectedFeedbackSourcesInstalled,
} from './OnboardingFeedbackSources';
import OnboardingIntegrations, { isSelectedIntegrationsInstalled } from './OnboardingIntegrations';
import OnboardingSyncIntegrations, {
  isSelectedSyncIntegrationsInstalled,
} from './OnboardingSyncIntegrations';
import {
  type OnboardingProduct,
  OnboardingProducts,
  type OnboardingStep,
  OnboardingStepStatus,
  OnboardingStepType,
  OnboardingStepVisibilityBehavior,
  OnboardingSteps,
} from './types';

import type { Onboarding } from 'common/api/endpoints/onboarding';
import type {
  IntegrationOptionValueKey,
  SourcesOptionValueKey,
} from 'common/subdomain/admin/AdminOnboarding/data';

const IDBFlag = 'idea-database';

export const onboardingStepConfig: Record<OnboardingSteps, OnboardingStep> = {
  [OnboardingSteps.createBoard]: {
    buttonTitle: 'Create a board',
    description: 'Give your customers a place to post and vote on feedback',
    href: '/admin/create-board',
    icon: SquareChevronUp,
    id: OnboardingSteps.createBoard,
    isCompleted: ({ boardCount }) => boardCount > 0,
    overflowDescription: 'Give your customers a place to post and vote on feedback',
    title: 'Create a board',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.createFirstIdeas]: {
    buttonTitle: 'Create',
    description: 'Track feedback that your hear from customers',
    href: '/admin/ideas',
    icon: Lightbulb,
    id: OnboardingSteps.createFirstIdeas,
    isDisabled: ({ featureAllowlist }) => !featureAllowlist.includes(IDBFlag),
    isCompleted: (_, { ideaStats }) => ideaStats.hasCreatedThreeIdeas,
    overflowDescription: 'Track feedback that your hear from customers',
    overflowTitle: 'Create ideas',
    title: 'Create your first 3 ideas',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.customizePortal]: {
    buttonTitle: 'Portal settings',
    description: 'Add your logo and brand colors',
    href: '/admin/settings/company/branding',
    id: OnboardingSteps.customizePortal,
    isCompleted: ({ faviconURL, logoURL, tintColor }) =>
      faviconURL !== null || logoURL !== null || tintColor !== StaticColors.canny,
    title: 'Customize your portal',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.enableFeedbackPortal]: {
    buttonTitle: 'View Portal',
    description: 'Your customers can post and vote on feedback in your portal',
    href: '/',
    id: OnboardingSteps.enableFeedbackPortal,
    isCompleted: () => true,
    buttonVisible: ({ boardCount }) => boardCount > 0,
    useCaseUpdatePayload: {
      visitedPortal: true,
    },
    title: 'Enable your feedback portal',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.importFeedback]: {
    buttonTitle: 'Upload',
    condition: (_, onboarding) =>
      !!onboarding.motivations?.includes(CollectFeedbackOptionValue.Import),
    description: 'Bring existing feedback as files or text',
    href: () => '/admin/getting-started?autopilotImport=true',
    icon: Import,
    id: OnboardingSteps.importFeedback,
    isCompleted: () => false,
    overflowDescription: 'Upload CSV files to pull in existing feedback',
    overflowTitle: 'Import',
    title: 'Import feedback',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.installFeedbackSources]: {
    condition: (_, onboarding) =>
      onboarding.communicationTools.some((tool) =>
        Object.values(SourcesOptionValue).includes(tool as SourcesOptionValueKey),
      ),
    description: 'Automatically capture feedback from your tools',
    href: '/admin/settings/integrations/feedback-boards',
    icon: Bot,
    id: OnboardingSteps.installFeedbackSources,
    isCompleted: isSelectedFeedbackSourcesInstalled,
    overflowDescription: 'Automatically capture feedback from customer conversations and calls',
    overflowTitle: 'Autopilot AI',
    renderContent: () => <OnboardingFeedbackSources />,
    title: 'Install feedback sources',
    type: OnboardingStepType.expandable,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.installIntegrations]: {
    condition: (_, onboarding) =>
      getAllIntegrations(onboarding).some((tool) =>
        Object.values(IntegrationOptionValue).includes(tool as IntegrationOptionValueKey),
      ),
    description: 'Integrate Canny with your existing workflows',
    href: '/admin/settings/integrations/all',
    icon: ChevronsLeftRightEllipsis,
    id: OnboardingSteps.installIntegrations,
    isCompleted: isSelectedIntegrationsInstalled,
    overflowDescription: 'Connect your project management tool, communication tools, etc.',
    overflowTitle: 'Integrations',
    renderContent: () => <OnboardingIntegrations />,
    title: 'Install integrations',
    type: OnboardingStepType.expandable,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.inviteTeam]: {
    buttonTitle: 'Invite team',
    description: 'Collaborate and get more eyes on customer feedback',
    href: '/admin/settings/team/people',
    icon: Users,
    id: OnboardingSteps.inviteTeam,
    isCompleted: ({ inviteCount, members }) => inviteCount > 0 || members.length > 1,
    title: 'Invite your team',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.publicReviews]: {
    buttonTitle: 'Setup',
    description: 'Capture feedback on sites like Capterra, G2, App Store, etc.',
    href: '/admin/settings/integrations/autopilot',
    icon: Star,
    id: OnboardingSteps.publicReviews,
    isCompleted: ({ activeIntegrations }) =>
      publicReviewIntegrations.some((integration) => activeIntegrations[integration]),
    title: 'Public reviews',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.publishFirstChangelogEntry]: {
    buttonTitle: 'Create entry',
    description: 'Share your latest product update',
    href: '/admin/changelog/create',
    id: OnboardingSteps.publishFirstChangelogEntry,
    isCompleted: (_, onboarding) => onboarding.changelogStats.hasPublishedEntries,
    title: 'Publish your first changelog entry',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.setupChangelogLabels]: {
    buttonTitle: 'Create labels',
    description: 'Bring existing feedback as files or text',
    href: '/admin/settings/changelog/labels',
    id: OnboardingSteps.setupChangelogLabels,
    isCompleted: (_, onboarding) => onboarding.changelogStats.hasLabels,
    title: 'Set up changelog labels',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.setupGroups]: {
    buttonTitle: 'Create',
    description: 'Canny automatically organizes feedback into groups',
    href: '/admin/ideas?createGroup=true',
    icon: Group,
    id: OnboardingSteps.setupGroups,
    isDisabled: ({ featureAllowlist }) => !featureAllowlist.includes(IDBFlag),
    isCompleted: (_, onboarding) => onboarding.ideaStats.hasCreatedGroups,
    overflowDescription: 'Organize feedback into product areas for clarity and prioritization',
    overflowTitle: 'Create groups',
    title: 'Set up your groups',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.syncUserData]: {
    description: 'Tie feedback to user data to better understand impact',
    href: '/admin/settings/integrations/all',
    id: OnboardingSteps.syncUserData,
    isCompleted: isSelectedSyncIntegrationsInstalled,
    renderContent: () => <OnboardingSyncIntegrations />,
    title: 'Sync user data',
    type: OnboardingStepType.expandable,
    status: OnboardingStepStatus.incomplete,
  },
  [OnboardingSteps.widget]: {
    buttonTitle: 'Install',
    description: 'Show feedback boards directly on your website',
    href: devURL('https://developers.canny.io/install/widget'),
    icon: Bot,
    id: OnboardingSteps.widget,
    isCompleted: ({ stats }) => stats.hasUsedIdentify || stats.hasUsedSSO,
    title: 'Widget',
    type: OnboardingStepType.button,
    status: OnboardingStepStatus.incomplete,
  },
};

const getAllIntegrations = (onboarding: Onboarding) => {
  return [
    ...onboarding.communicationTools,
    ...onboarding.internalTools,
    ...onboarding.projectManagementTools,
  ];
};

export const onboardingProductConfig: Record<OnboardingProducts, OnboardingProduct> = {
  [OnboardingProducts.autopilot]: {
    condition: (_, onboarding) =>
      onboarding.customerFeedback ||
      onboarding.teammateFeedback ||
      onboarding.motivations?.some((motivation) =>
        [MotivationOptionValue.Feedback, MotivationOptionValue.Roadmap].includes(
          motivation as MotivationOptionValue,
        ),
      ),
    description: 'Automatically capture feedback from your sources',
    id: OnboardingProducts.ideas,
    isDisabled: ({ featureAllowlist }) => featureAllowlist.includes(IDBFlag),
    name: 'Autopilot',
    useCaseSetupPayload: {
      motivations: [MotivationOptionValue.Feedback],
    },
    steps: [
      {
        id: OnboardingSteps.installFeedbackSources,
        visibilityBehavior: OnboardingStepVisibilityBehavior.conditional,
      },
      {
        id: OnboardingSteps.installIntegrations,
        visibilityBehavior: OnboardingStepVisibilityBehavior.conditional,
      },
      {
        id: OnboardingSteps.syncUserData,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
    ],
  },
  [OnboardingProducts.ideas]: {
    condition: (_, onboarding) =>
      onboarding.customerFeedback ||
      onboarding.teammateFeedback ||
      onboarding.motivations?.some((motivation) =>
        [MotivationOptionValue.Feedback, MotivationOptionValue.Roadmap].includes(
          motivation as MotivationOptionValue,
        ),
      ),
    description: 'Organize your feedback to understand impact',
    id: OnboardingProducts.ideas,
    isDisabled: ({ featureAllowlist }) => !featureAllowlist.includes(IDBFlag),
    name: 'Ideas',
    useCaseSetupPayload: {
      motivations: [MotivationOptionValue.Feedback],
    },
    steps: [
      {
        id: OnboardingSteps.setupGroups,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.createFirstIdeas,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.installFeedbackSources,
        visibilityBehavior: OnboardingStepVisibilityBehavior.conditional,
      },
      {
        id: OnboardingSteps.importFeedback,
        visibilityBehavior: OnboardingStepVisibilityBehavior.conditional,
      },
      {
        id: OnboardingSteps.installIntegrations,
        visibilityBehavior: OnboardingStepVisibilityBehavior.conditional,
      },
      {
        id: OnboardingSteps.syncUserData,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.inviteTeam,
        visibilityBehavior: OnboardingStepVisibilityBehavior.overflow,
      },
      {
        id: OnboardingSteps.publicReviews,
        visibilityBehavior: OnboardingStepVisibilityBehavior.overflow,
      },
    ],
  },
  [OnboardingProducts.feedbackPortal]: {
    condition: (_, onboarding) =>
      onboarding.publicFeedback ||
      onboarding.motivations?.some(
        (motivation) => motivation === CollectFeedbackOptionValue.Direct,
      ),
    description: 'Let users post and vote on feedback',
    id: OnboardingProducts.feedbackPortal,
    name: 'Feedback Portal',
    useCaseSetupPayload: {
      motivations: [CollectFeedbackOptionValue.Direct],
    },
    steps: [
      {
        id: OnboardingSteps.enableFeedbackPortal,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.createBoard,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.customizePortal,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.installIntegrations,
        visibilityBehavior: OnboardingStepVisibilityBehavior.conditional,
      },
      {
        id: OnboardingSteps.syncUserData,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.setupGroups,
        visibilityBehavior: OnboardingStepVisibilityBehavior.overflow,
      },
      {
        id: OnboardingSteps.widget,
        visibilityBehavior: OnboardingStepVisibilityBehavior.overflow,
      },
      {
        id: OnboardingSteps.inviteTeam,
        visibilityBehavior: OnboardingStepVisibilityBehavior.overflow,
      },
    ],
  },
  [OnboardingProducts.announcements]: {
    condition: (_, onboarding) =>
      onboarding.motivations.some(
        (motivation) => motivation === MotivationOptionValue.Announcements,
      ),
    description: 'Share latest updates with your users',
    id: OnboardingProducts.announcements,
    name: 'Announcements',
    useCaseSetupPayload: {
      motivations: [MotivationOptionValue.Announcements],
    },
    steps: [
      {
        id: OnboardingSteps.enableFeedbackPortal,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.customizePortal,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.setupChangelogLabels,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.publishFirstChangelogEntry,
        visibilityBehavior: OnboardingStepVisibilityBehavior.always,
      },
      {
        id: OnboardingSteps.createFirstIdeas,
        visibilityBehavior: OnboardingStepVisibilityBehavior.overflow,
      },
      {
        id: OnboardingSteps.setupGroups,
        visibilityBehavior: OnboardingStepVisibilityBehavior.overflow,
      },
      {
        id: OnboardingSteps.createBoard,
        visibilityBehavior: OnboardingStepVisibilityBehavior.overflow,
      },
    ],
  },
};
